import React, { useEffect, useState, useRef } from 'react';
import { Button } from './Button';
import FavoriteButton from './FavoriteButton';
import { PlayIcon, PauseIcon, VolumeIcon, Volume2Icon, Volume1Icon, VolumeXIcon, ArrowLeftIcon, MicIcon, CloudIcon, TreePineIcon, WavesIcon, CoffeeIcon, SpeakerIcon, PianoIcon, SparklesIcon, MoreHorizontalIcon } from 'lucide-react';
interface SoundscapeProps {
  onBack: () => void;
}
const soundscapes = [{
  id: 'rain',
  title: 'Zachte Regen',
  description: 'Rustgevende regengeluiden voor focus en ontspanning',
  duration: '30 min',
  category: 'natuur',
  icon: <CloudIcon className="w-8 h-8 text-gray-700" data-id="element-1234" />,
  pattern: 'dotted'
}, {
  id: 'forest',
  title: 'Boswandeling',
  description: 'Vogels, wind door bomen en zachte voetstappen',
  duration: '45 min',
  category: 'natuur',
  icon: <TreePineIcon className="w-8 h-8 text-gray-700" data-id="element-1235" />,
  pattern: 'dashed'
}, {
  id: 'waves',
  title: 'Oceaangolven',
  description: 'Rustige golven die zachtjes op het strand breken',
  duration: '60 min',
  category: 'natuur',
  icon: <WavesIcon className="w-8 h-8 text-gray-700" data-id="element-1236" />,
  pattern: 'wavy'
}, {
  id: 'cafe',
  title: 'Koffiehuis Ambiance',
  description: 'Zachte gesprekken en koffiezetgeluiden',
  duration: '40 min',
  category: 'omgeving',
  icon: <CoffeeIcon className="w-8 h-8 text-gray-700" data-id="element-1237" />,
  pattern: 'circular'
}, {
  id: 'white-noise',
  title: 'Witte Ruis',
  description: 'Constante ruis om afleiding te maskeren',
  duration: '120 min',
  category: 'focus',
  icon: <SpeakerIcon className="w-8 h-8 text-gray-700" data-id="element-1238" />,
  pattern: 'static'
}, {
  id: 'piano',
  title: 'Rustige Piano',
  description: 'Zachte pianomuziek voor diepe ontspanning',
  duration: '35 min',
  category: 'muziek',
  icon: <PianoIcon className="w-8 h-8 text-gray-700" data-id="element-1239" />,
  pattern: 'linear'
}];
const Soundscape: React.FC<SoundscapeProps> = ({
  onBack
}) => {
  const [currentSound, setCurrentSound] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [volume, setVolume] = useState(70);
  const [activeCategory, setActiveCategory] = useState('alle');
  const [isListening, setIsListening] = useState(false);
  const [showVoiceUI, setShowVoiceUI] = useState(false);
  const [ambientBackground, setAmbientBackground] = useState('');
  const [moodRecommendation, setMoodRecommendation] = useState('');
  const visualizerRef = useRef<HTMLDivElement>(null);
  const categories = [{
    id: 'alle',
    label: 'Alle'
  }, {
    id: 'natuur',
    label: 'Natuur'
  }, {
    id: 'omgeving',
    label: 'Omgeving'
  }, {
    id: 'focus',
    label: 'Focus'
  }, {
    id: 'muziek',
    label: 'Muziek'
  }];
  const filteredSoundscapes = activeCategory === 'alle' ? soundscapes : soundscapes.filter(sound => sound.category === activeCategory);
  useEffect(() => {
    if (currentSound) {
      // Simulate ambient background changes based on selected soundscape
      const soundscape = soundscapes.find(s => s.id === currentSound);
      setAmbientBackground(soundscape?.id || '');
      // Simulate visualizer animation
      if (isPlaying && visualizerRef.current) {
        // In a real implementation, this would be an actual audio visualization
        // For wireframe purposes, we're just indicating where it would be
      }
    }
  }, [currentSound, isPlaying]);
  useEffect(() => {
    // Simulate mood-based recommendation
    setMoodRecommendation('Gebaseerd op je laatste check-in, zou Zachte Regen goed bij je stemming passen');
  }, []);
  const handleSoundSelect = (soundId: string) => {
    if (currentSound === soundId) {
      setIsPlaying(!isPlaying);
    } else {
      setCurrentSound(soundId);
      setIsPlaying(true);
    }
  };
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setVolume(parseInt(e.target.value));
  };
  const getVolumeIcon = () => {
    if (volume === 0) return <VolumeXIcon className="w-5 h-5 text-gray-600" data-id="element-1240" />;
    if (volume < 30) return <VolumeIcon className="w-5 h-5 text-gray-600" data-id="element-1241" />;
    if (volume < 70) return <Volume1Icon className="w-5 h-5 text-gray-600" data-id="element-1242" />;
    return <Volume2Icon className="w-5 h-5 text-gray-600" data-id="element-1243" />;
  };
  const getCurrentSoundscape = () => {
    return soundscapes.find(s => s.id === currentSound);
  };
  const toggleVoiceUI = () => {
    setShowVoiceUI(!showVoiceUI);
    if (!showVoiceUI) {
      setIsListening(true);
      // Simulate voice recognition
      setTimeout(() => {
        setIsListening(false);
      }, 3000);
    }
  };
  const getPatternStyle = (pattern: string) => {
    switch (pattern) {
      case 'dotted':
        return 'bg-gray-100 border-2 border-dotted border-gray-300';
      case 'dashed':
        return 'bg-gray-100 border-2 border-dashed border-gray-300';
      case 'wavy':
        return 'bg-gray-100 border-2 border-gray-300';
      case 'circular':
        return 'bg-gray-100 border-2 border-gray-300 rounded-full';
      case 'static':
        return 'bg-gray-200 border-2 border-gray-300';
      case 'linear':
        return 'bg-gray-100 border-2 border-gray-300';
      default:
        return 'bg-gray-100 border-2 border-gray-300';
    }
  };
  return <div className="w-full h-full bg-white relative" data-id="element-1244">
      {/* Ambient background - would be animated in a real implementation */}
      {ambientBackground && <div className="absolute inset-0 pointer-events-none opacity-10" data-id="element-1245">
          <div className={`w-full h-full ${ambientBackground === 'rain' ? 'bg-gray-200' : 'bg-gray-100'}`} data-id="element-1246">
            {ambientBackground === 'rain' && <div className="absolute inset-0 flex flex-wrap gap-2 opacity-20" data-id="element-1247">
                {Array.from({
            length: 20
          }).map((_, i) => <div key={i} className="w-px h-16 bg-gray-400" style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            opacity: Math.random() * 0.5 + 0.5
          }} data-id="element-1248"></div>)}
              </div>}
          </div>
        </div>}
      <div className="w-full max-w-4xl mx-auto py-8 px-4 relative z-10" data-id="element-1249">
        <div className="flex justify-between items-center mb-8" data-id="element-1250">
          <div className="flex items-center" data-id="element-1251">
            <button onClick={onBack} className="mr-4 p-2 rounded-full hover:bg-gray-100" aria-label="Terug" data-id="element-1252">
              <ArrowLeftIcon className="w-5 h-5 text-gray-600" data-id="element-1253" />
            </button>
            <h1 className="text-2xl font-light text-gray-800" data-id="element-1254">Soundscapes</h1>
          </div>
          <div className="flex items-center space-x-2" data-id="element-1255">
            <button onClick={toggleVoiceUI} className={`p-3 rounded-full ${isListening ? 'bg-gray-200 animate-pulse' : 'bg-gray-100 hover:bg-gray-200'}`} aria-label="Voice control" data-id="element-1256">
              <MicIcon className="w-5 h-5 text-gray-700" data-id="element-1257" />
            </button>
          </div>
        </div>
        {/* Voice UI overlay */}
        {showVoiceUI && <div className="mb-6 p-5 border-2 border-gray-300 rounded-xl bg-gray-50" data-id="element-1258">
            <div className="flex items-center mb-3" data-id="element-1259">
              <MicIcon className={`w-5 h-5 mr-3 ${isListening ? 'text-gray-700 animate-pulse' : 'text-gray-600'}`} data-id="element-1260" />
              <p className="text-gray-700 font-medium" data-id="element-1261">
                {isListening ? 'Luisteren...' : 'Zeg bijvoorbeeld "Start Boswandeling"'}
              </p>
            </div>
            {!isListening && <div className="flex flex-wrap gap-2 mt-2" data-id="element-1262">
                <span className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600" data-id="element-1263">
                  "Start Zachte Regen"
                </span>
                <span className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600" data-id="element-1264">
                  "Pauzeer soundscape"
                </span>
                <span className="px-3 py-1 bg-gray-200 rounded-full text-xs text-gray-600" data-id="element-1265">
                  "Volume verhogen"
                </span>
              </div>}
          </div>}
        {/* Mood recommendation */}
        {moodRecommendation && !currentSound && <div className="mb-8 p-5 border-2 border-gray-300 rounded-xl" data-id="element-1266">
            <div className="flex items-start" data-id="element-1267">
              <SparklesIcon className="w-5 h-5 text-gray-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-1268" />
              <div data-id="element-1269">
                <p className="text-sm text-gray-700 font-medium mb-1" data-id="element-1270">
                  Aanbevolen voor je stemming
                </p>
                <p className="text-sm text-gray-600" data-id="element-1271">{moodRecommendation}</p>
              </div>
            </div>
          </div>}
        {/* Current soundscape player */}
        {currentSound && <div className="p-8 border-2 border-gray-300 rounded-xl mb-10 relative overflow-hidden" data-id="element-1272">
            <div className="flex flex-col md:flex-row items-center relative z-10" data-id="element-1273">
              <div className="w-full md:w-1/3 mb-6 md:mb-0 md:mr-6" data-id="element-1274">
                <div className={`aspect-square rounded-xl overflow-hidden flex items-center justify-center ${getPatternStyle(getCurrentSoundscape()?.pattern || 'dotted')}`} data-id="element-1275">
                  {getCurrentSoundscape()?.icon || <Volume2Icon className="w-20 h-20 text-gray-700" data-id="element-1276" />}
                  {/* Visualizer overlay - would be animated in real implementation */}
                  <div ref={visualizerRef} className={`absolute inset-0 pointer-events-none ${isPlaying ? 'opacity-50' : 'opacity-0'}`} data-id="element-1277">
                    <div className="flex items-center justify-center h-full" data-id="element-1278">
                      <div className="w-32 h-32 text-gray-500" data-id="element-1279" />
                    </div>
                  </div>
                </div>
              </div>
              <div className="w-full md:w-2/3" data-id="element-1280">
                <div className="flex justify-between items-start mb-4" data-id="element-1281">
                  <div data-id="element-1282">
                    <h2 className="text-xl font-medium text-gray-800 mb-1" data-id="element-1283">
                      {getCurrentSoundscape()?.title}
                    </h2>
                    <p className="text-sm text-gray-600 mb-4" data-id="element-1284">
                      {getCurrentSoundscape()?.description}
                    </p>
                  </div>
                  <FavoriteButton data-id="element-1285" />
                </div>
                <div className="flex items-center justify-center mb-8" data-id="element-1286">
                  <Button size="large" onClick={() => setIsPlaying(!isPlaying)} leftIcon={isPlaying ? <PauseIcon className="w-5 h-5" data-id="element-1288" /> : <PlayIcon className="w-5 h-5" data-id="element-1289" />} data-id="element-1287">
                    {isPlaying ? 'Pauzeren' : 'Afspelen'}
                  </Button>
                </div>
                <div className="flex items-center mb-2" data-id="element-1290">
                  {getVolumeIcon()}
                  <input type="range" min="0" max="100" value={volume} onChange={handleVolumeChange} className="w-full mx-4 h-2 bg-gray-200 rounded-full appearance-none" data-id="element-1291" />
                  <span className="text-xs text-gray-600" data-id="element-1292">{volume}%</span>
                </div>
                <div className="text-xs text-gray-500 text-right" data-id="element-1293">
                  Duur: {getCurrentSoundscape()?.duration}
                </div>
              </div>
            </div>
          </div>}
        {/* Category filters */}
        <div className="mb-8 overflow-x-auto" data-id="element-1294">
          <div className="flex space-x-4 pb-2" data-id="element-1295">
            {categories.map(category => <button key={category.id} className={`px-5 py-2 rounded-full text-sm whitespace-nowrap border-2 transition-all duration-300 ${activeCategory === category.id ? 'bg-gray-200 border-gray-400 text-gray-800 scale-105' : 'bg-gray-50 border-gray-300 text-gray-600 hover:bg-gray-100'}`} onClick={() => setActiveCategory(category.id)} data-id="element-1296">
                {category.label}
              </button>)}
          </div>
        </div>
        {/* Soundscape grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-1297">
          {filteredSoundscapes.map(sound => <div key={sound.id} className={`p-5 border-2 border-gray-300 rounded-xl cursor-pointer transition-all duration-300 relative overflow-hidden ${currentSound === sound.id && isPlaying ? 'bg-gray-50 border-gray-400 scale-[1.02]' : 'hover:bg-gray-50 hover:scale-[1.01]'}`} onClick={() => handleSoundSelect(sound.id)} data-id="element-1298">
              {/* Pattern background - would be animated in real implementation */}
              <div className="absolute inset-0 opacity-5 pointer-events-none" data-id="element-1299">
                {sound.pattern === 'dotted' && <div className="absolute inset-0 grid grid-cols-10 grid-rows-10" data-id="element-1300">
                    {Array.from({
                length: 100
              }).map((_, i) => <div key={i} className="flex items-center justify-center" data-id="element-1301">
                        <div className="w-1 h-1 rounded-full bg-gray-500" data-id="element-1302"></div>
                      </div>)}
                  </div>}
                {sound.pattern === 'wavy' && <div className="absolute inset-0 flex items-center" data-id="element-1303">
                    <svg width="100%" height="40" viewBox="0 0 100 20" preserveAspectRatio="none" data-id="element-1304">
                      <path d="M0,10 Q25,20 50,10 T100,10" fill="none" stroke="gray" strokeWidth="0.5" data-id="element-1305" />
                    </svg>
                  </div>}
              </div>
              <div className="flex items-start relative z-10" data-id="element-1306">
                <div className={`w-16 h-16 rounded-xl flex-shrink-0 mr-4 flex items-center justify-center ${getPatternStyle(sound.pattern)}`} data-id="element-1307">
                  {sound.icon}
                </div>
                <div className="flex-1" data-id="element-1308">
                  <div className="flex justify-between" data-id="element-1309">
                    <h3 className="text-base font-medium text-gray-800 mb-1" data-id="element-1310">
                      {sound.title}
                    </h3>
                    <FavoriteButton size="sm" data-id="element-1311" />
                  </div>
                  <p className="text-xs text-gray-600 mb-3" data-id="element-1312">
                    {sound.description}
                  </p>
                  <div className="flex justify-between items-center" data-id="element-1313">
                    <span className="text-xs text-gray-500" data-id="element-1314">
                      {sound.duration}
                    </span>
                    {currentSound === sound.id && isPlaying ? <PauseIcon className="w-5 h-5 text-gray-600" data-id="element-1315" /> : <PlayIcon className="w-5 h-5 text-gray-600" data-id="element-1316" />}
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </div>
    </div>;
};
export default Soundscape;